<template>
  <router-link
    v-if="visible"
    class="question-sets-sidebar-link"
    :class="{ active, mastery, rail, mobile }"
    :to="{ name: 'questionSets#index' }"
    @click="clicked"
  >
    <span
      class="sparkles"
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 24 24"
        :width="iconSize"
        :height="iconSize"
      >
        <defs>
          <linearGradient
            id="question-sets-sparkle"
            x1="0"
            y1="0"
            x2="1"
            y2="1"
          >
            <stop
              offset="0%"
              stop-color="#A78BFA"
            />
            <stop
              offset="50%"
              stop-color="#60A5FA"
            />
            <stop
              offset="100%"
              stop-color="#F472B6"
            />
          </linearGradient>
        </defs>
        <path
          class="star star-large"
          fill="url(#question-sets-sparkle)"
          d="M10 2c.4 3.9 2.1 5.6 6 6-3.9.4-5.6 2.1-6 6-.4-3.9-2.1-5.6-6-6 3.9-.4 5.6-2.1 6-6z"
        />
        <path
          class="star star-small"
          fill="url(#question-sets-sparkle)"
          d="M18 13c.25 2.35 1.25 3.35 3.6 3.6-2.35.25-3.35 1.25-3.6 3.6-.25-2.35-1.25-3.35-3.6-3.6 2.35-.25 3.35-1.25 3.6-3.6z"
        />
        <path
          class="star star-tiny"
          fill="url(#question-sets-sparkle)"
          d="M19 2.5c.15 1.4.75 2 2.1 2.1-1.35.15-1.95.75-2.1 2.1-.15-1.35-.75-1.95-2.1-2.1 1.35-.1 1.95-.7 2.1-2.1z"
        />
      </svg>
    </span>
    <span class="name">My Questions</span>
    <span class="beta">Beta</span>
  </router-link>
</template>

<script>
export default {
  props: {
    // Icon-first layout for the mastery sidebar's narrow rail; the name shows when the rail expands
    rail: {
      type: Boolean,
      default: false,
    },
    // Full-width row for the mastery mobile menu
    mobile: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      viewed: false,
    };
  },
  computed: {
    // Rail and sidebar links are seen once they render; the mobile link only when the menu is open
    impressionReady() {
      return this.visible && (!this.mobile || this.$store.getters['session/showMobileMenu']);
    },
    source() {
      if (this.mobile) return 'mobile_menu';
      return this.rail ? 'sidebar_rail' : 'sidebar';
    },
    iconSize() {
      if (this.rail) return 24;
      return this.mobile ? 20 : 18;
    },
    active() {
      return this.$route.name?.startsWith('questionSets#');
    },
    mastery() {
      return this.$store.getters['app/isMasteryApp'];
    },
    visible() {
      return this.$store.getters['questionSets/visible'];
    },
  },
  watch: {
    impressionReady: {
      immediate: true,
      handler(ready) {
        if (ready && !this.viewed) {
          this.viewed = true;
          this.$analytics.events.viewedMyQuestionsEntry({
            source: this.source,
            consentPending: this.$store.getters['questionSets/consentPending'],
          });
        } else if (!ready && this.mobile) {
          // Count each opening of the mobile menu
          this.viewed = false;
        }
      },
    },
  },
  methods: {
    clicked() {
      this.$analytics.events.selectedMyQuestions({
        source: this.source,
        consentPending: this.$store.getters['questionSets/consentPending'],
      });
      this.$store.commit('session/toggleMobileMenu', false);
    },
  },
};
</script>

<style lang="scss" scoped>
  .question-sets-sidebar-link {
    align-items: center;
    border-radius: 4px;
    display: flex;
    gap: rem-calc(8);
    margin-bottom: rem-calc(4);
    min-height: rem-calc(37);
    padding: 0 rem-calc(12);
    text-decoration: none;

    &:hover {
      background-image: linear-gradient(to bottom, #2F3845, #252C35);
    }

    &.active {
      background-image: linear-gradient(-180deg, rgba(81, 97, 115, .48) 3%, rgba(59, 72, 87, .48) 98%);
    }

    .sparkles {
      display: flex;
      filter: drop-shadow(0 0 3px rgba(167, 139, 250, .6));
    }

    .star {
      animation: twinkle 2.4s ease-in-out infinite;
      transform-box: fill-box;
      transform-origin: center;
    }

    .star-small {
      animation-delay: .8s;
    }

    .star-tiny {
      animation-delay: 1.6s;
    }

    .name {
      animation: shimmer 4s linear infinite;
      background: linear-gradient(90deg, #C4B5FD, #93C5FD, #F9A8D4, #C4B5FD);
      background-clip: text;
      background-size: 300% 100%;
      -webkit-background-clip: text;
      color: transparent;
      font-size: rem-calc(12);
      font-weight: $semi-bold;
    }

    .beta {
      border: 1px solid rgba(196, 181, 253, .6);
      border-radius: 999px;
      color: #C4B5FD;
      font-size: rem-calc(9);
      font-weight: $semi-bold;
      letter-spacing: .04em;
      margin-left: auto;
      padding: 0 rem-calc(6);
      text-transform: uppercase;
    }

    &.mastery {
      &:hover,
      &.active {
        background: #F1ECFE;
      }

      .name {
        background: linear-gradient(90deg, #7C3AED, #2563EB, #DB2777, #7C3AED);
        background-clip: text;
        background-size: 300% 100%;
        -webkit-background-clip: text;
      }

      .beta {
        border-color: rgba(124, 58, 237, .5);
        color: #7C3AED;
      }
    }

    &.rail {
      flex-direction: column;
      gap: rem-calc(4);
      height: auto;
      min-height: 0;
      padding: rem-calc(8) 0;
      position: relative;
      width: 100%;

      &:hover,
      &.active {
        background: transparent;
      }

      .sparkles {
        filter: drop-shadow(0 0 4px rgba(124, 58, 237, .45));
      }

      .name {
        display: none;
        font-weight: $normal;
        text-align: center;
      }

      &.active .name {
        font-weight: $semi-bold;
      }

      .beta {
        font-size: rem-calc(8);
        left: 50%;
        margin: 0;
        position: absolute;
        top: rem-calc(-6);
        transform: translateX(-50%);
      }
    }

    &.mobile {
      gap: rem-calc(8);
      min-height: 0;
      padding: 0;

      &:hover,
      &.active {
        background: transparent;
      }

      .name {
        font-size: inherit;
        font-weight: inherit;
      }

      .beta {
        margin-left: rem-calc(4);
        margin-right: auto;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .star,
      .name {
        animation: none;
      }
    }
  }

  @keyframes twinkle {
    0%, 100% {
      opacity: 1;
      transform: scale(1) rotate(0deg);
    }

    50% {
      opacity: .45;
      transform: scale(.7) rotate(20deg);
    }
  }

  @keyframes shimmer {
    to {
      background-position: 300% 0;
    }
  }
</style>
